/**
 * PATH: apps/quote/src/lib/quoteApi.ts
 * PURPOSE: Barrel file — re-exports types and composes API modules into a single quoteApi object.
 *
 * The actual implementations live in:
 *   - ./api/quotes-crud-api.ts        (quotes CRUD, sections, reference data, CRM, claims)
 *   - ./api/line-items-api.ts         (line items, materials, bulk operations)
 *   - ./api/ai-features-api.ts        (AI suggestions, audit, optimization)
 *   - ./api/import-dashboard-api.ts   (import/export, dashboard stats)
 *   - ./api/pricing-api.ts            (pricebooks, supplier quotes, xero, historical pricing)
 *   - ./api/quote-context-api.ts      (attachments, context, history, other charges)
 *   - ./api/supplier-brochure-api.ts  (brochures, price sources, quote requests)
 *   - ./api/assembly-takeoff-api.ts   (assemblies, takeoff assistant)
 *   - ./api/spreadsheet-api.ts        (spreadsheet state engine, bindings)
 *   - ./api/spreadsheet-template-api.ts (templates, workbook validation)
 *
 * Type definitions live in:
 *   - ./quote-api-types.ts            (core quote/section/line-item types)
 *   - ./quote-api-claims-types.ts     (claims, charges, attachments, context)
 *   - ./quote-api-reference-types.ts  (dashboard, supplier brochure, import/export)
 *   - ./quote-api-ai-types.ts         (AI intelligence, reference data)
 *   - ./quote-api-assembly-types.ts   (assembly, takeoff)
 *   - ./quote-api-spreadsheet-types.ts (spreadsheet engine, templates, validation)
 *   - ./quote-api-pricing-types.ts    (pricebook, supplier quote, xero, price suggestions)
 */

// Re-export all types so existing `import { QuoteDetail } from './quoteApi'` still works
export * from './quote-api-types'
export * from './quote-api-claims-types'
export * from './quote-api-reference-types'
export * from './quote-api-ai-types'
export * from './quote-api-assembly-types'
export * from './quote-api-spreadsheet-types'
export * from './quote-api-pricing-types'

// Import API modules
import { quotesCrudApi } from './api/quotes-crud-api'
import { lineItemsApi } from './api/line-items-api'
import { aiFeaturesApi } from './api/ai-features-api'
import { importDashboardApi } from './api/import-dashboard-api'
import { pricingApi } from './api/pricing-api'
import { quoteContextApi } from './api/quote-context-api'
import { supplierBrochureApi } from './api/supplier-brochure-api'
import { assemblyTakeoffApi } from './api/assembly-takeoff-api'
import { spreadsheetApi } from './api/spreadsheet-api'
import { spreadsheetTemplateApi } from './api/spreadsheet-template-api'
import { snapshotsApi } from './api/snapshots-api'

// Re-export snapshot types alongside the rest so consumers can grab
// `import { QuoteSnapshotListItem } from '@/lib/quoteApi'`.
export type {
  CreateSnapshotRequest,
  QuoteSnapshotDetail,
  QuoteSnapshotListItem,
  QuoteSnapshotTotals,
  SnapshotSource,
} from './api/snapshots-api'

// Compose all modules into the single quoteApi object
// This preserves backward compatibility: `quoteApi.listQuotes()` still works
export const quoteApi = {
  ...quotesCrudApi,
  ...lineItemsApi,
  ...aiFeaturesApi,
  ...importDashboardApi,
  ...pricingApi,
  ...quoteContextApi,
  ...supplierBrochureApi,
  ...assemblyTakeoffApi,
  ...spreadsheetApi,
  ...spreadsheetTemplateApi,
  // Namespaced under `snapshots` to keep the verbs (list/create/get/remove)
  // from colliding with the flat-spread quote-CRUD verbs above.
  snapshots: snapshotsApi,
}
